// @ts-check
import HTTP from '@/utils/http';
import { EventBus } from '@/utils/event-bus';
import i18n from '@/locales';

import {
  BulkSend,
  ImportedRows,
  Parties,
  Preview,
  Templates,
  Usage,
} from '@/store/modules/bulkSend/models';
import { Sharing } from '@/store/modules/users/models';

const defaultState = () => ({
  /** @type {?import('zod').z.infer<typeof BulkSend>} */
  bulkSend: null,
  /** @type {?import('zod').z.infer<typeof Sharing>} */
  sharing: null,
  /** @type {?import('zod').z.infer<typeof Templates>} */
  templates: null,
  /** @type {?import('zod').z.infer<typeof ImportedRows>} */
  importedRows: null,
  /** @type {?import('zod').z.infer<typeof Parties>} */
  parties: null,
});

/** @type {import("vuex").MutationTree<ReturnType<typeof defaultState>>} */
const mutations = {
  RESET_STATE(state) {
    Object.assign(state, defaultState());
  },
  SET_BULKSEND(state, bulkSend) {
    state.bulkSend = bulkSend;
  },
  SET_SHARING(state, sharing) {
    state.sharing = sharing;
  },
  SET_TEMPLATES(state, templates) {
    state.templates = templates;
  },
  SET_IMPORTED_ROWS(state, importedRows) {
    state.importedRows = importedRows;
  },
  SET_PARTIES(state, parties) {
    state.parties = parties;
  },
};

/** @type {import("vuex").ActionTree<typeof defaultState>} */
const actions = {
  async fetchTheBulkSend({ commit }, fingerprint) {
    try {
      const response = await HTTP({
        url: `bulk-sends/${fingerprint}`,
        suppressAllErrors: true,
      });

      commit('SET_BULKSEND', BulkSend.parse(response.data.data));
    } catch (error) {
      // show user custom message when not found
      if (error.response) {
        EventBus.$emit('renderGlobalMessage', {
          title: i18n.t('functional.error'),
          message: error.response.status === 404 ? i18n.t('bulkSend.notFound') : error.response.message || error.response.statusText,
          type: 'error',
        });
      }

      throw error;
    }
  },
  async createBulkSend({ commit }, data) {
    const response = await HTTP({
      url: 'bulk-sends',
      method: 'POST',
      data: { name: data.name },
    });

    const bulkSend = BulkSend.parse(response.data.data);

    commit('SET_BULKSEND', bulkSend);

    return bulkSend;
  },
  async updateBulkSend({ commit }, { fingerprint, data }) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}`,
      method: 'PATCH',
      data,
    });

    commit('SET_BULKSEND', BulkSend.parse(response.data.data));
  },
  async updateProgress(_, { fingerprint, data }) {
    HTTP({
      url: `bulk-sends/${fingerprint}`,
      method: 'PATCH',
      data: { steps_progress: data },
    });
  },
  async fetchSharing({ commit }, fingerprint) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/sharing`,
    });

    commit('SET_SHARING', Sharing.parse(response.data.data));
  },
  async updateSharing({ commit }, { fingerprint, data }) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/sharing`,
      method: 'POST',
      data,
    });

    commit('SET_SHARING', Sharing.parse(response.data.data));
  },
  async fetchTemplates({ commit }, fingerprint) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/templates`,
    });

    commit('SET_TEMPLATES', Templates.parse(response.data.data));
  },
  async updateTemplates({ commit }, { fingerprint, data }) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/templates`,
      method: 'POST',
      data: { template_ids: data },
    });

    commit('SET_TEMPLATES', Templates.parse(response.data.data));
  },
  async uploadImport({ commit }, { fingerprint, data }) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/import`,
      method: 'POST',
      data,
    });

    commit('SET_BULKSEND', BulkSend.parse(response.data.data));
  },
  async fetchImportedRows({ commit }, fingerprint) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/imported-rows`,
    });

    commit('SET_IMPORTED_ROWS', ImportedRows.parse(response.data.data));
  },
  async fetchParties({ commit }, fingerprint) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/parties`,
    });

    commit('SET_PARTIES', Parties.parse(response.data.data));
  },
  async updateParties({ commit }, { fingerprint, data }) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/parties`,
      method: 'PUT',
      data: { parties: data },
    });

    commit('SET_PARTIES', Parties.parse(response.data.data));
  },
  async fetchPreview(context, fingerprint) {
    const response = await HTTP({
      url: `bulk-sends/imported-contacts/${fingerprint}/preview-url`,
    });

    return Preview.parse(response.data.data);
  },
  async fetchUsage(_, fingerprint) {
    const response = await HTTP({
      url: `bulk-sends/${fingerprint}/usage`,
    });

    return Usage.parse(response.data.data);
  },
  async fetchBulkSend({ commit, dispatch }, fingerprint) {
    commit('RESET_STATE');

    await dispatch('fetchTheBulkSend', fingerprint);
    await Promise.all([
      dispatch('fetchSharing', fingerprint),
      dispatch('fetchTemplates', fingerprint),
      dispatch('fetchParties', fingerprint),
    ]);
  },
  resetBulkSend({ commit }) {
    commit('RESET_STATE');
  },
};

const getters = {
  /** @param {ReturnType<defaultState>} state */
  bulkSend: state => ({
    ...state.bulkSend,
    sharing: state.sharing,
    templates: state.templates,
    parties: state.parties,
  }),
  /** @param {ReturnType<defaultState>} state */
  stepsProgress: state => state.bulkSend?.steps_progress,
  /** @param {ReturnType<defaultState>} state */
  loaded: state =>
    !!state.bulkSend &&
    !!state.sharing &&
    !!state.templates &&
    !!state.parties,
  /** @param {ReturnType<defaultState>} state */
  isSent: state => !!state.bulkSend?.sent_at,
};

export default {
  namespaced: true,
  state: defaultState,
  mutations,
  actions,
  getters,
};
